import React from 'react';
import { Shield, Lock, Eye, Database, Share2, Cookie } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy - GENMOZPRO"
        description="GENMOZPRO Privacy Policy. Learn how we collect, use, and protect your personal information. AdSense compliant privacy policy for payment testing tools."
        keywords="privacy policy, data protection, genmozpro privacy, gdpr, user data, cookie policy"
        canonical="https://genmozpro.com/privacy"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4 sm:mb-6">
            <Shield className="w-3 sm:w-4 h-3 sm:h-4 text-blue-500" />
            <span className="text-blue-500 text-xs sm:text-sm font-medium">Privacy Policy</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            Privacy <span className="text-blue-500">Policy</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">
            Last updated: {new Date().toLocaleDateString('en-US')}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span>1. Introduction</span>
            </h2>
            <p className="text-gray-300 mb-4">
              GENMOZPRO ("we", "our" or "platform") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store and protect your information when you use our payment system testing services.
            </p>
            <p className="text-gray-300">
              By using our platform, you agree to the collection and use of information in accordance with this policy. This policy complies with the General Data Protection Law (LGPD - Law 13.709/2018) and the General Data Protection Regulation (GDPR).
            </p>
          </section>

          {/* Data Collection */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Database className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span>2. Information We Collect</span>
            </h2>
            
            <h3 className="text-lg font-semibold mb-3 text-blue-400">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Email address for newsletter and communications</li>
              <li>Test card data (for demonstration purposes only)</li>
              <li>Account preferences and settings</li>
              <li>Support messages and feedback</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3 text-blue-400">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>IP address and approximate geographic location</li>
              <li>Browser type and device</li>
              <li>Pages visited and time spent</li>
              <li>Cookies and similar technologies</li>
              <li>Usage logs and performance statistics</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3 text-blue-400">2.3 Test Data</h3>
            <p className="text-gray-300">
              All generated card numbers are fictitious and created using the Luhn algorithm for testing purposes. We do not collect, store or process real credit card information.
            </p>
          </section>

          {/* Data Usage */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span>3. How We Use Your Information</span>
            </h2>
            <p className="text-gray-300 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Provide, maintain and improve our services</li>
              <li>Process your requests and transactions</li>
              <li>Send updates, newsletters and promotional materials</li>
              <li>Respond to questions and provide technical support</li>
              <li>Analyze usage and trends to improve user experience</li>
              <li>Detect, prevent and resolve technical and security issues</li>
              <li>Comply with legal and regulatory obligations</li>
              <li>Display relevant advertising through Google AdSense</li>
            </ul>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span>4. Cookies and Tracking Technologies</span>
            </h2>
            <p className="text-gray-300 mb-4">
              We use cookies and similar technologies to enhance your experience, analyze traffic, and personalize content and ads. You can control cookie usage through your browser settings.
            </p>
            
            <h3 className="text-lg font-semibold mb-3 text-blue-400">Types of Cookies We Use:</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
              <li><strong>Performance Cookies:</strong> Collect information about how you use our site</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and choices</li>
              <li><strong>Advertising Cookies:</strong> Google AdSense and partners to display relevant ads</li>
            </ul>
          </section>

          {/* Google AdSense */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">5. Google AdSense and Advertising</h2>
            <p className="text-gray-300 mb-4">
              We use Google AdSense to display ads on our site. Google uses cookies to display ads based on users' previous visits to our site or other sites.
            </p>
            <p className="text-gray-300 mb-4">
              Users can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Ads Settings</a>. Alternatively, you can opt out of third-party cookies by visiting the <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Network Advertising Initiative opt-out page</a>.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span>6. Data Sharing</span>
            </h2>
            <p className="text-gray-300 mb-4">We do not sell your personal information. We may share your data with:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li><strong>Service Providers:</strong> Companies that help us operate our platform</li>
              <li><strong>Google AdSense:</strong> For displaying personalized ads</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              <li><strong>Analytics Partners:</strong> For usage analysis and metrics</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">7. Data Security</h2>
            <p className="text-gray-300 mb-4">
              We implement technical and organizational security measures to protect your information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure storage on protected servers</li>
              <li>Restricted access to personal data</li>
              <li>Continuous security monitoring</li>
              <li>Regular backups and disaster recovery plans</li>
            </ul>
          </section>

          {/* User Rights */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">8. Your Rights (LGPD/GDPR)</h2>
            <p className="text-gray-300 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Access your personal data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data</li>
              <li>Revoke consent at any time</li>
              <li>Data portability</li>
              <li>Object to data processing</li>
              <li>File a complaint with a supervisory authority</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To exercise these rights, contact us at: <a href="mailto:privacy@genmozpro.com" className="text-blue-400 hover:underline">privacy@genmozpro.com</a>
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">9. Data Retention</h2>
            <p className="text-gray-300">
              We retain your personal information only as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required or permitted by law. Test data is automatically deleted after 30 days.
            </p>
          </section>

          {/* International Transfers */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">10. International Transfers</h2>
            <p className="text-gray-300">
              Your data may be transferred and processed in countries outside Brazil. We ensure all transfers are made with adequate protections, including standard contractual clauses approved by the National Data Protection Authority (ANPD).
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">11. Children's Privacy</h2>
            <p className="text-gray-300">
              Our services are not intended for individuals under 18 years of age. We do not intentionally collect personal information from children. If you believe we have collected data from a minor, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8 bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy periodically. We will notify you of significant changes by posting the new policy on our site and updating the "Last updated" date. We recommend reviewing this page regularly.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">13. Contact</h2>
            <p className="text-gray-300 mb-4">
              If you have questions about this Privacy Policy or our data practices:
            </p>
            <div className="space-y-2 text-gray-300">
              <p><strong>Email:</strong> <a href="mailto:privacy@genmozpro.com" className="text-blue-400 hover:underline">privacy@genmozpro.com</a></p>
              <p><strong>Data Protection Officer (DPO):</strong> <a href="mailto:dpo@genmozpro.com" className="text-blue-400 hover:underline">dpo@genmozpro.com</a></p>
              <p><strong>Address:</strong> GENMOZPRO Platform Services</p>
            </div>
          </section>
        </div>
      </div>
      </div>
    </>
  );
};

export default Privacy;